import type { ThroughputSnapshot } from "./types";

const THROUGHPUT_BUCKETS = 10; // 10 minutes of history
const STORAGE_KEY = "nsq-diagnostic-throughput";
const FLUSH_DEBOUNCE_MS = 5000;

interface StoredSourceEntry {
  buckets: number[];
  currentMinute: number;
}

interface StoredThroughput {
  version: 1;
  sources: Record<string, StoredSourceEntry>;
}

/**
 * Lightweight per-source message throughput counters.
 * Tracks messages received per minute, bucketed by source.
 *
 * Persists to localStorage with a debounced flush (every 5s after last write)
 * and a synchronous flush on beforeunload. Hydrates from localStorage on
 * construction, rolling forward any stale minute offsets so counters resume
 * correctly after a reload.
 */
class ThroughputTracker {
  private sources = new Map<
    string,
    { buckets: number[]; currentMinute: number }
  >();
  private flushTimer: ReturnType<typeof setTimeout> | undefined;
  private dirty = false;
  private storageAvailable: boolean;

  constructor() {
    this.storageAvailable = this.probeStorage();
    this.hydrateFromStorage();
    window.addEventListener("beforeunload", () => this.flushSync());
  }

  /**
   * Increment counter for a source. Called on every SSE message.
   */
  increment(source: string): void {
    const now = Math.floor(Date.now() / 60000); // current minute

    let entry = this.sources.get(source);
    if (!entry) {
      entry = {
        buckets: new Array(THROUGHPUT_BUCKETS).fill(0),
        currentMinute: now,
      };
      this.sources.set(source, entry);
    }

    // Roll forward if minutes have passed
    const minutesDiff = now - entry.currentMinute;
    if (minutesDiff > 0) {
      if (minutesDiff >= THROUGHPUT_BUCKETS) {
        entry.buckets.fill(0);
      } else {
        for (let i = THROUGHPUT_BUCKETS - 1; i >= minutesDiff; i--) {
          entry.buckets[i] = entry.buckets[i - minutesDiff];
        }
        for (let i = 0; i < minutesDiff; i++) {
          entry.buckets[i] = 0;
        }
      }
      entry.currentMinute = now;
    }

    entry.buckets[0]++;

    this.dirty = true;
    this.scheduleDebouncedFlush();
  }

  /**
   * Snapshot all tracked sources as throughput data for the report.
   */
  getSnapshots(): ThroughputSnapshot[] {
    const now = Math.floor(Date.now() / 60000);
    const snapshots: ThroughputSnapshot[] = [];

    for (const [source, entry] of this.sources) {
      const minutesDiff = now - entry.currentMinute;
      let buckets: number[];

      if (minutesDiff >= THROUGHPUT_BUCKETS) {
        buckets = new Array(THROUGHPUT_BUCKETS).fill(0);
      } else if (minutesDiff > 0) {
        buckets = [...entry.buckets];
        for (let i = THROUGHPUT_BUCKETS - 1; i >= minutesDiff; i--) {
          buckets[i] = buckets[i - minutesDiff];
        }
        for (let i = 0; i < minutesDiff; i++) {
          buckets[i] = 0;
        }
      } else {
        buckets = [...entry.buckets];
      }

      snapshots.push({ source, buckets, bucketWindowMinutes: 1 });
    }

    return snapshots;
  }

  /**
   * Immediately flush the in-memory state to localStorage.
   * Called on beforeunload and available for external callers.
   */
  flushSync(): void {
    if (this.flushTimer !== undefined) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
    this.flushToStorage();
  }

  private probeStorage(): boolean {
    const testKey = "__nsq_throughput_probe__";
    try {
      localStorage.setItem(testKey, "1");
      localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private hydrateFromStorage(): void {
    if (!this.storageAvailable) return;

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const stored: StoredThroughput = JSON.parse(raw);
      if (stored.version !== 1 || !stored.sources) return;

      const now = Math.floor(Date.now() / 60000);

      for (const [source, entry] of Object.entries(stored.sources)) {
        if (
          !entry ||
          !Array.isArray(entry.buckets) ||
          typeof entry.currentMinute !== "number"
        ) {
          continue;
        }

        // Drop entries entirely stale (older than the retention window).
        // Fresh but partially-stale entries get rolled forward on next increment()/getSnapshots().
        const minutesDiff = now - entry.currentMinute;
        if (minutesDiff >= THROUGHPUT_BUCKETS) continue;

        // Normalise bucket length in case the constant has changed across versions.
        const buckets = new Array(THROUGHPUT_BUCKETS).fill(0);
        for (
          let i = 0;
          i < Math.min(THROUGHPUT_BUCKETS, entry.buckets.length);
          i++
        ) {
          buckets[i] = entry.buckets[i] ?? 0;
        }

        this.sources.set(source, {
          buckets,
          currentMinute: entry.currentMinute,
        });
      }
    } catch {
      // Corrupted JSON or unexpected shape — start fresh
    }
  }

  private scheduleDebouncedFlush(): void {
    if (!this.storageAvailable) return;
    if (this.flushTimer !== undefined) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => {
      this.flushTimer = undefined;
      this.flushToStorage();
    }, FLUSH_DEBOUNCE_MS);
  }

  private flushToStorage(): void {
    if (!this.dirty || !this.storageAvailable) return;

    try {
      const sources: Record<string, StoredSourceEntry> = {};
      for (const [source, entry] of this.sources) {
        sources[source] = {
          buckets: entry.buckets,
          currentMinute: entry.currentMinute,
        };
      }

      const payload: StoredThroughput = {
        version: 1,
        sources,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      this.dirty = false;
    } catch {
      // Quota exceeded — disable persistence for the rest of this session
      this.storageAvailable = false;
    }
  }
}

/** Global singleton */
export const throughputTracker = new ThroughputTracker();
